// Third Party
import * as React from 'react';
import { ResizeObserver as PolyfillObserver } from '@juggle/resize-observer';
import { v4 } from 'uuid';

// Utils
import { NotificationType } from '.';

// UI
import { NotificationCard } from './notification-card';

const weakMap: WeakMap<object, string> = new WeakMap();
const keygen = (obj: any): string => {
  // Find or create and return UUID
  const key = weakMap.get(obj as object);

  if (key) return key;
  else {
    weakMap.set(obj as object, v4());
    return weakMap.get(obj as object)!;
  }
};

export type NotificationPayload = {
  uniqueId: string;
  message?: string;
  timeout?: number;
  title: string;
  type: NotificationType;
};

export const NotificationOverlay: React.FC<{
  markAllForRemoval?: boolean;
  notifications: NotificationPayload[];
  removeNotification: (notificationId: string) => void;
}> = ({ markAllForRemoval, notifications, removeNotification }) => {
  // Absolute positioned elements can't reference their parent container's size in css;
  // we need the absolute element to be the height of the entire document for the desired
  // sticky behavior at 768px+
  const [documentHeight, setDocumentHeight] = React.useState<number>(0);
  React.useEffect(() => {
    const rootDiv = document.getElementById('__next');

    let resizeObserver: ResizeObserver;

    if (rootDiv) {
      const ResizeObserver = window.ResizeObserver || PolyfillObserver;

      resizeObserver = new ResizeObserver(() =>
        setDocumentHeight(rootDiv.getBoundingClientRect().bottom)
      );
      resizeObserver.observe(rootDiv);
    }

    return () => resizeObserver && resizeObserver.disconnect();
  }, []);

  if (!notifications.length) return null;

  return (
    <div
      className="md:top-20 absolute w-full pointer-events-none md:px-8"
      style={{ height: documentHeight, zIndex: 1000 }}
    >
      <div
        aria-live="assertive"
        className="bottom-6 md:top-20 fixed left-0 right-0 flex flex-row mx-auto md:sticky md:justify-end"
      >
        <div className="flex flex-col-reverse items-center px-2 w-full space-y-2 space-y-reverse md:flex-col md:items-end md:px-0 md:space-y-2">
          <>
            {notifications.map((notification) => (
              <NotificationCard
                key={keygen(notification)}
                markForRemoval={markAllForRemoval}
                uniqueId={notification.uniqueId}
                message={notification.message}
                timeout={notification.timeout}
                title={notification.title}
                type={notification.type}
                removeNotification={removeNotification}
              />
            ))}
          </>
        </div>
      </div>
    </div>
  );
};
